import React, { useEffect, useState, useRef } from "react";
import Topbar from "../components/Topbar";
import { supabase } from "../lib/supabase";
import { useToast } from "../components/ui/Feedback";
import {
  Search,
  Pencil,
  Trash2,
  Plus,
  X,
  ImagePlus,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { friendlyError } from "../lib/errorMessage";

const emptyAdForm = {
  title: "",
  link_url: "",
  start_date: "",
  end_date: "",
  is_active: true,
};

const BUCKET = "ads-images";

// Extracts the storage path from a public Supabase URL, e.g.
// https://xxx.supabase.co/storage/v1/object/public/ads-images/abc.jpg -> abc.jpg
function getStoragePathFromUrl(url: string): string | null {
  const marker = `/object/public/${BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return url.substring(idx + marker.length);
}

async function deleteImageFromStorage(url: string | null | undefined) {
  if (!url) return;
  const path = getStoragePathFromUrl(url);
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) console.error("Failed to delete storage image:", error.message);
}

async function uploadAdImage(file: File): Promise<string> {
  const ext = file.name.split(".").pop();
  const fileName = `${crypto.randomUUID()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(fileName, file, { cacheControl: "3600", upsert: false });

  if (uploadError) {
    throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
  return data.publicUrl;
}

function SingleImageDropzone({
  existingImage,
  newPreview,
  onSelectFile,
  onRemoveExisting,
  onRemoveNew,
}: {
  existingImage?: string | null;
  newPreview: string | null;
  onSelectFile: (file: File) => void;
  onRemoveExisting?: () => void;
  onRemoveNew: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = Array.from(e.dataTransfer.files ?? []).find((f) =>
      f.type.startsWith("image/")
    );
    if (file) onSelectFile(file);
  }

  function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onSelectFile(file);
    e.target.value = "";
  }

  const previewSrc = newPreview || existingImage || null;

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `1.5px dashed ${
            dragOver ? "var(--primary)" : "var(--border, #d9dce3)"
          }`,
          borderRadius: 12,
          padding: previewSrc ? 0 : "22px 16px",
          textAlign: "center",
          cursor: "pointer",
          background: dragOver ? "var(--primary-light)" : "var(--bg)",
          transition: "all 0.15s ease",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handlePick}
        />

        {previewSrc ? (
          <div style={{ position: "relative" }}>
            <img
              src={previewSrc}
              alt=""
              style={{
                width: "100%",
                height: 140,
                objectFit: "cover",
                display: "block",
              }}
            />
            {newPreview && (
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#fff",
                  background: "var(--primary)",
                  padding: "2px 6px",
                  borderRadius: 5,
                }}
              >
                NEW
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (newPreview) onRemoveNew();
                else onRemoveExisting?.();
              }}
              style={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 26,
                height: 26,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.55)",
                border: "none",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ) : (
          <>
            <ImagePlus
              size={22}
              color="var(--primary)"
              style={{ marginBottom: 6 }}
            />
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              Click to upload or drag and drop
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginTop: 2,
              }}
            >
              Banner image, recommended ~1200x400px
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Ads() {
  const toast = useToast();
  const [ads, setAds] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // --- Edit state ---
  const [editing, setEditing] = useState<any | null>(null);
  const [editValues, setEditValues] = useState(emptyAdForm);
  const [editExistingImage, setEditExistingImage] = useState<string | null>(
    null
  );
  const [editNewFile, setEditNewFile] = useState<File | null>(null);
  const [editNewPreview, setEditNewPreview] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const [editError, setEditError] = useState("");

  // --- Add state ---
  const [adding, setAdding] = useState(false);
  const [newAd, setNewAd] = useState(emptyAdForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    fetchAds();
  }, []);

  async function fetchAds() {
    const { data } = await supabase
      .from("ads")
      .select("*")
      .order("created_at", { ascending: false });
    setAds(data ?? []);
    setLoading(false);
  }

  async function deleteAd(id: string, image_url?: string) {
    if (!window.confirm("Delete this ad?")) return;
    const { error } = await supabase.from("ads").delete().eq("id", id);
    if (error) {
      // Same shape as the product delete: the image cleanup ran regardless, so
      // a denied delete stripped the creative off a live ad.
      toast.failure(friendlyError(error, "Something went wrong. Please try again."));
      return;
    }
    if (image_url) await deleteImageFromStorage(image_url);
    toast.success("Ad deleted");
    setAds((a) => a.filter((x) => x.id !== id));
  }

  async function toggleActive(ad: any) {
    const { error } = await supabase
      .from("ads")
      .update({ is_active: !ad.is_active })
      .eq("id", ad.id);
    if (error) return;
    setAds((prev) =>
      prev.map((x) => (x.id === ad.id ? { ...x, is_active: !x.is_active } : x))
    );
  }

  // ---------- Add ad ----------

  function handleImageSelect(file: File) {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function removeNewImage() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  }

  function closeAddPanel() {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setAdding(false);
    setNewAd(emptyAdForm);
    setImageFile(null);
    setImagePreview(null);
    setFormError("");
  }

  async function addAd() {
    setFormError("");

    if (!imageFile) {
      setFormError("An image is required.");
      return;
    }

    setSaving(true);
    setUploading(true);
    let imageUrl = "";
    try {
      imageUrl = await uploadAdImage(imageFile);
    } catch (err: any) {
      setFormError(err.message ?? "Image upload failed.");
      setSaving(false);
      setUploading(false);
      return;
    }
    setUploading(false);

    const payload = {
      title: newAd.title.trim() || null,
      image_url: imageUrl,
      link_url: newAd.link_url.trim() || null,
      is_active: newAd.is_active,
      start_date: newAd.start_date || null,
      end_date: newAd.end_date || null,
    };

    const { data, error } = await supabase
      .from("ads")
      .insert(payload)
      .select()
      .single();
    setSaving(false);

    if (error) {
      setFormError(error.message);
      return;
    }

    if (data) setAds((a) => [data, ...a]);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setNewAd(emptyAdForm);
    setImageFile(null);
    setImagePreview(null);
    setAdding(false);
  }

  // ---------- Edit ad ----------

  const openEdit = (ad: any) => {
    setEditing(ad);
    setEditError("");
    setEditValues({
      title: ad.title ?? "",
      link_url: ad.link_url ?? "",
      start_date: ad.start_date ? ad.start_date.substring(0, 10) : "",
      end_date: ad.end_date ? ad.end_date.substring(0, 10) : "",
      is_active: ad.is_active ?? true,
    });
    setEditExistingImage(ad.image_url ?? null);
    setEditNewFile(null);
    setEditNewPreview(null);
  };

  function handleEditImageSelect(file: File) {
    if (editNewPreview) URL.revokeObjectURL(editNewPreview);
    setEditNewFile(file);
    setEditNewPreview(URL.createObjectURL(file));
  }

  function removeExistingEditImage() {
    setEditExistingImage(null);
  }

  function removeNewEditImage() {
    if (editNewPreview) URL.revokeObjectURL(editNewPreview);
    setEditNewFile(null);
    setEditNewPreview(null);
  }

  function closeEditModal() {
    if (editNewPreview) URL.revokeObjectURL(editNewPreview);
    setEditing(null);
    setEditNewFile(null);
    setEditNewPreview(null);
    setEditError("");
  }

  async function saveEdit() {
    if (!editing) return;
    setEditError("");

    if (!editExistingImage && !editNewFile) {
      setEditError("An image is required.");
      return;
    }

    setEditSaving(true);

    const originalImage: string | null = editing.image_url ?? null;
    const imageWasRemoved =
      originalImage && originalImage !== editExistingImage && !editNewFile
        ? originalImage
        : originalImage && editNewFile
        ? originalImage
        : null;

    let finalImageUrl = editExistingImage;
    if (editNewFile) {
      setEditUploading(true);
      try {
        finalImageUrl = await uploadAdImage(editNewFile);
      } catch (err: any) {
        setEditError(err.message ?? "Image upload failed.");
        setEditSaving(false);
        setEditUploading(false);
        return;
      }
      setEditUploading(false);
    }

    const updates = {
      title: editValues.title.trim() || null,
      link_url: editValues.link_url.trim() || null,
      image_url: finalImageUrl,
      is_active: editValues.is_active,
      start_date: editValues.start_date || null,
      end_date: editValues.end_date || null,
    };

    const { error } = await supabase
      .from("ads")
      .update(updates)
      .eq("id", editing.id);
    setEditSaving(false);

    if (error) {
      setEditError(error.message);
      return;
    }

    if (imageWasRemoved) await deleteImageFromStorage(imageWasRemoved);

    setAds((a) =>
      a.map((x) => (x.id === editing.id ? { ...x, ...updates } : x))
    );
    if (editNewPreview) URL.revokeObjectURL(editNewPreview);
    setEditing(null);
    setEditNewFile(null);
    setEditNewPreview(null);
  }

  const formatDate = (d: string | null | undefined) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filtered = ads.filter(
    (a) =>
      (a.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (a.link_url ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <Topbar title="Ads" />
      <div style={{ padding: "24px 28px" }}>
        <div className="page-header" style={{ marginBottom: 18 }}>
          <div>
            <div className="page-title">Ads</div>
            <div className="page-sub">
              Manage banner ads shown in the app from your Supabase ads table
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setAdding(true);
              setFormError("");
            }}
          >
            <Plus size={15} /> Add Ad
          </button>
        </div>

        {/* Edit Modal */}
        {editing && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 200,
            }}
          >
            <div
              className="card"
              style={{
                width: 460,
                padding: 28,
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>
                Edit Ad
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    Title (internal label)
                  </label>
                  <input
                    className="input"
                    style={{ marginTop: 4 }}
                    value={editValues.title}
                    onChange={(e) =>
                      setEditValues((v) => ({ ...v, title: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    Link URL
                  </label>
                  <input
                    className="input"
                    style={{ marginTop: 4 }}
                    value={editValues.link_url}
                    onChange={(e) =>
                      setEditValues((v) => ({
                        ...v,
                        link_url: e.target.value,
                      }))
                    }
                    placeholder="https://..."
                  />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 12,
                        color: "var(--text-muted)",
                        fontWeight: 600,
                      }}
                    >
                      Start Date
                    </label>
                    <input
                      className="input"
                      type="date"
                      style={{ marginTop: 4 }}
                      value={editValues.start_date}
                      onChange={(e) =>
                        setEditValues((v) => ({
                          ...v,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: 12,
                        color: "var(--text-muted)",
                        fontWeight: 600,
                      }}
                    >
                      End Date
                    </label>
                    <input
                      className="input"
                      type="date"
                      style={{ marginTop: 4 }}
                      value={editValues.end_date}
                      onChange={(e) =>
                        setEditValues((v) => ({
                          ...v,
                          end_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 2px",
                  }}
                >
                  <label
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    Active
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setEditValues((v) => ({ ...v, is_active: !v.is_active }))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: editValues.is_active
                        ? "var(--primary)"
                        : "var(--text-muted)",
                    }}
                  >
                    {editValues.is_active ? (
                      <ToggleRight size={26} />
                    ) : (
                      <ToggleLeft size={26} />
                    )}
                  </button>
                </div>

                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    Banner Image
                  </label>
                  <div style={{ marginTop: 4 }}>
                    <SingleImageDropzone
                      existingImage={editExistingImage}
                      newPreview={editNewPreview}
                      onSelectFile={handleEditImageSelect}
                      onRemoveExisting={removeExistingEditImage}
                      onRemoveNew={removeNewEditImage}
                    />
                  </div>
                </div>
              </div>

              {editError && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "var(--danger-light)",
                    color: "var(--danger)",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {editError}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={saveEdit}
                  disabled={editSaving}
                >
                  {editUploading
                    ? "Uploading image..."
                    : editSaving
                    ? "Saving..."
                    : "Save Changes"}
                </button>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "var(--bg)",
                    color: "var(--text-secondary)",
                  }}
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {adding && (
          <div
            className="card"
            style={{ marginBottom: 18, borderLeft: "4px solid var(--primary)" }}
          >
            <div className="page-header" style={{ marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>Add New Ad</div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginTop: 2,
                  }}
                >
                  Fields match your live `ads` table.
                </div>
              </div>
              <button
                className="btn btn-sm"
                style={{ background: "var(--bg)", border: "none" }}
                onClick={closeAddPanel}
              >
                <X size={14} /> Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 14,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  Title (internal label)
                </label>
                <input
                  className="input"
                  style={{ marginTop: 5 }}
                  value={newAd.title}
                  onChange={(e) =>
                    setNewAd((v) => ({ ...v, title: e.target.value }))
                  }
                  placeholder="e.g. Summer Promo"
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  Link URL
                </label>
                <input
                  className="input"
                  style={{ marginTop: 5 }}
                  value={newAd.link_url}
                  onChange={(e) =>
                    setNewAd((v) => ({ ...v, link_url: e.target.value }))
                  }
                  placeholder="https://..."
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  Start Date (optional)
                </label>
                <input
                  className="input"
                  type="date"
                  style={{ marginTop: 5 }}
                  value={newAd.start_date}
                  onChange={(e) =>
                    setNewAd((v) => ({ ...v, start_date: e.target.value }))
                  }
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  End Date (optional)
                </label>
                <input
                  className="input"
                  type="date"
                  style={{ marginTop: 5 }}
                  value={newAd.end_date}
                  onChange={(e) =>
                    setNewAd((v) => ({ ...v, end_date: e.target.value }))
                  }
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  Banner Image *
                </label>
                <div style={{ marginTop: 5 }}>
                  <SingleImageDropzone
                    newPreview={imagePreview}
                    onSelectFile={handleImageSelect}
                    onRemoveNew={removeNewImage}
                  />
                </div>
              </div>

              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "4px 2px",
                }}
              >
                <label
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    fontWeight: 700,
                  }}
                >
                  Active
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setNewAd((v) => ({ ...v, is_active: !v.is_active }))
                  }
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: newAd.is_active
                      ? "var(--primary)"
                      : "var(--text-muted)",
                  }}
                >
                  {newAd.is_active ? (
                    <ToggleRight size={26} />
                  ) : (
                    <ToggleLeft size={26} />
                  )}
                </button>
              </div>
            </div>

            {formError && (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "var(--danger-light)",
                  color: "var(--danger)",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {formError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 18,
              }}
            >
              <button
                className="btn"
                style={{
                  background: "var(--bg)",
                  color: "var(--text-secondary)",
                }}
                onClick={closeAddPanel}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={addAd}
                disabled={saving}
              >
                <Plus size={15} />{" "}
                {uploading
                  ? "Uploading image..."
                  : saving
                  ? "Adding..."
                  : "Add Ad"}
              </button>
            </div>
          </div>
        )}

        <div className="card">
          <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search
                size={14}
                color="var(--text-muted)"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                className="input"
                placeholder="Search ads..."
                style={{ paddingLeft: 34 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th>Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ad) => (
                  <tr key={ad.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        {ad.image_url ? (
                          <img
                            src={ad.image_url}
                            alt=""
                            style={{
                              width: 56,
                              height: 36,
                              borderRadius: 8,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 56,
                              height: 36,
                              borderRadius: 8,
                              background: "var(--primary-light)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 14,
                            }}
                          >
                            🖼️
                          </div>
                        )}
                        <span
                          style={{
                            fontWeight: 600,
                            color: "var(--text-primary)",
                          }}
                        >
                          {ad.title || "Untitled ad"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleActive(ad)}
                        className={`badge ${
                          ad.is_active ? "badge-primary" : ""
                        }`}
                        style={{
                          border: "none",
                          cursor: "pointer",
                          background: ad.is_active
                            ? undefined
                            : "var(--bg)",
                          color: ad.is_active ? undefined : "var(--text-muted)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        title="Click to toggle"
                      >
                        {ad.is_active ? (
                          <ToggleRight size={13} />
                        ) : (
                          <ToggleLeft size={13} />
                        )}
                        {ad.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {ad.start_date || ad.end_date
                        ? `${formatDate(ad.start_date)} – ${formatDate(
                            ad.end_date
                          )}`
                        : "Always on"}
                    </td>
                    <td
                      style={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 12.5,
                      }}
                    >
                      {ad.link_url || "—"}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "var(--primary-light)",
                            color: "var(--primary)",
                            border: "none",
                          }}
                          onClick={() => openEdit(ad)}
                          title="Edit"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteAd(ad.id, ad.image_url)}
                          title="Delete"
                          style={{ border: "none" }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: "center",
                        color: "var(--text-muted)",
                        padding: 40,
                      }}
                    >
                      No ads found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}